import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../app/AppContext';
import {
  MINIMUM_VIEWPORT,
  PROTOCOL_VERSION,
  getEnvironmentConditionId,
  getSessionEnvironmentConditionId,
  visualDegreesToPixels,
} from '../core';
import type { ModuleId, SessionCompletionPayload, TrialResult } from '../core/types';
import { TrainingSession, startingDifficultyFromSummary } from '../training';
import { trainingStimuliFitViewport } from '../training/display-fit';

const MODULE_IDS: readonly ModuleId[] = ['read-switch', 'central-peripheral', 'player-track', 'moving-target'];

export function TrainingPage() {
  const app = useApp();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const requestedModule = params.get('module');
  const moduleId = MODULE_IDS.includes(requestedModule as ModuleId) ? requestedModule as ModuleId : undefined;
  const resumeId = params.get('resume');
  const resumeSession = resumeId ? app.sessions.find((session) => session.id === resumeId) : undefined;
  const mode = 'training' as const;
  const moduleIds = resumeSession?.moduleIds ?? (moduleId ? [moduleId] : [...MODULE_IDS]);
  const parsedFatigue = Number(params.get('fatigue'));
  const fatigue = (Number.isFinite(parsedFatigue) ? Math.min(5, Math.max(1, Math.round(parsedFatigue))) : 3) as 1 | 2 | 3 | 4 | 5;
  const [resumeTrials, setResumeTrials] = useState<TrialResult[] | null | undefined>(resumeId ? undefined : []);
  const [resumeRetry, setResumeRetry] = useState(0);
  const admitted = useRef(false);

  useEffect(() => {
    if (!resumeId || !resumeSession?.id) return;
    let active = true;
    void app.loadSessionTrials(resumeId).then((trials) => {
      if (active) setResumeTrials(trials);
    }).catch(() => {
      if (active) setResumeTrials(null);
    });
    return () => { active = false; };
  }, [app.loadSessionTrials, resumeId, resumeRetry, resumeSession?.id]);

  if (!app.calibration || !app.environment) return <Navigate to="/calibration" replace />;
  const environment = app.environment;
  const environmentConditionId = getEnvironmentConditionId(environment);
  const completedNewestFirst = app.sessions
    .filter((session) => session.status === 'completed')
    .sort((left, right) => right.startedAt.localeCompare(left.startedAt));
  const initialDifficulties = Object.fromEntries(moduleIds.map((selectedModuleId) => {
    const latestSession = completedNewestFirst.find((session) =>
      session.protocolVersion === PROTOCOL_VERSION
      && getSessionEnvironmentConditionId(session) === environmentConditionId
      && (app.summariesBySession[session.id] ?? []).some((summary) => summary.moduleId === selectedModuleId));
    const summary = latestSession
      ? app.summariesBySession[latestSession.id]?.find((item) => item.moduleId === selectedModuleId)
      : undefined;
    return [selectedModuleId, startingDifficultyFromSummary(summary)];
  }));

  const currentWidth = window.innerWidth;
  const currentHeight = window.innerHeight;
  const pixelsPerDegree = visualDegreesToPixels(
    1,
    app.calibration.viewingDistanceCm,
    app.calibration.pixelsPerCentimeter,
  );
  const viewportReady = currentWidth >= MINIMUM_VIEWPORT.width && currentHeight >= MINIMUM_VIEWPORT.height;
  const visualFieldReady = trainingStimuliFitViewport(currentWidth, currentHeight, pixelsPerDegree);
  const dprReady = Math.abs(window.devicePixelRatio - environment.devicePixelRatio) <= 0.05;
  const resumeReady = !resumeId || Boolean(
    resumeSession
    && resumeSession.status === 'in-progress'
    && resumeSession.environmentId === environment.id
    && resumeSession.protocolVersion === PROTOCOL_VERSION,
  );
  const preflightReady = params.get('correction') === '1'
    && Boolean(document.fullscreenElement)
    && viewportReady
    && visualFieldReady
    && dprReady
    && resumeReady;

  if (preflightReady) admitted.current = true;
  if (!admitted.current) return <Navigate to={moduleId ? `/session?module=${moduleId}` : '/session'} replace />;
  if (resumeId && resumeTrials === null) {
    return <div className="app-loading" role="alert"><strong>中断記録を読み込めませんでした</strong><small>データを上書きせず停止しています</small><button className="button" type="button" onClick={() => { setResumeTrials(undefined); setResumeRetry((value) => value + 1); }}>読込を再試行</button></div>;
  }
  if (resumeId && resumeTrials === undefined) {
    return <div className="app-loading" role="status"><span aria-hidden="true" /><strong>中断した記録を読み込んでいます</strong><small>そのままお待ちください</small></div>;
  }

  return (
    <TrainingSession
      mode={mode}
      moduleIds={moduleIds}
      initialDifficulties={initialDifficulties}
      calibration={app.calibration}
      environment={environment}
      fatigue={resumeSession?.fatigue ?? fatigue}
      correctionConfirmed
      resume={resumeSession && resumeTrials ? { session: resumeSession, trials: resumeTrials } : undefined}
      onStart={app.saveSessionStart}
      onTrial={app.saveTrial}
      onAbort={app.saveAbort}
      onExit={() => {
        navigate('/');
        if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
      }}
      onComplete={async (payload: SessionCompletionPayload) => {
        await app.saveCompletion(payload);
        navigate(`/results/${payload.session.id}`);
        if (document.fullscreenElement) await document.exitFullscreen().catch(() => undefined);
      }}
    />
  );
}
